import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PenLine } from "lucide-react";
import { Eyebrow, FadeUp } from "../site/Motion";
import { Stars } from "./Stars";
import { ReviewForm } from "./ReviewForm";
import { api, imgUrl } from "../../lib/api";

const ReviewCard = ({ r, i }) => (
  <FadeUp delay={(i % 3) * 0.08} className="flex h-full flex-col overflow-hidden rounded-[24px] border border-[#D4AF37]/20 bg-[#330B2E]/70" data-testid={`review-card-${i}`}>
    {r.photo_url && <img src={imgUrl(r.photo_url)} alt={`${r.name}'s ${r.category_name}`} loading="lazy" className="aspect-[4/3] w-full object-cover" />}
    <div className="flex flex-1 flex-col gap-3 p-5">
      <Stars value={r.rating} />
      <p className="flex-1 text-sm leading-relaxed text-[#FFF8EF]">"{r.text}"</p>
      <p className="font-cinzel text-xs tracking-[0.2em] text-[#D4AF37]">{r.name} · {new Date(r.created_at).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}</p>
    </div>
  </FadeUp>
);

export const CategoryReviews = ({ cat }) => {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["reviews", cat.slug], queryFn: () => api.get("/reviews", { params: { category: cat.slug } }).then((r) => r.data), retry: false });
  const rows = Array.isArray(data) ? data : [];
  const avg = rows.length ? rows.reduce((s, r) => s + r.rating, 0) / rows.length : 0;

  return (
    <section className="bg-[#240620] py-14 sm:py-20" data-testid="category-reviews">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Eyebrow>Customer reviews</Eyebrow>
            <h2 className="mt-3 font-display text-4xl text-[#FFF8EF] sm:text-5xl">Loved by our customers</h2>
            <div className="mt-2 flex items-center gap-2 text-sm text-[#E8D8C8]" data-testid="reviews-summary">
              {rows.length ? <><Stars value={Math.round(avg)} /> {avg.toFixed(1)} from {rows.length} review{rows.length > 1 ? "s" : ""}</> : `Be the first to review our ${cat.name}.`}
            </div>
          </div>
          <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 self-start rounded-full bg-gold-foil px-6 py-3 text-sm font-semibold text-[#240620] transition-transform duration-300 hover:scale-105 sm:self-auto" data-testid="write-review-btn">
            <PenLine size={16} /> Write a review
          </button>
        </div>
        {rows.length > 0 && (
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="reviews-grid">
            {rows.map((r, i) => <ReviewCard key={r.id} r={r} i={i} />)}
          </div>
        )}
      </div>
      <ReviewForm open={open} setOpen={setOpen} cat={cat} />
    </section>
  );
};
